import {
  CacheUpdateEvent,
  CacheDeleteEvent,
} from '@/rec-cache/rec-cache-events'

class TypeSelection {
  constructor(typeMap, ids, refreshFn) {
    console.log('rec-cache.rec-cache-type-selector.type-selection.constructor')
    this.types = []
    this._selection = []
    this.typeMap = typeMap
    this.ids = ids
    this.refreshFn = refreshFn

    // Initial update, if no types are set by query then whole set of ids is returned.
    this._selection = [...this.ids.values()]
    if (this.refreshFn) this.refreshFn(this._selection)
  }

  get selection() {
    return this._selection
  }

  // Parameter types is an array of type values (fully expanded).
  query(types) {
    // console.log('rec-cache.rec-cache-type-selector.type-selection.query', types)
    if (types.length == 0) {
      // If types array is empty return all ids
      this.types = []
      this._selection = [...this.ids.values()]
      if (this.refreshFn) this.refreshFn(this._selection)
      return this._selection
    }
    if (
      types.length == this.types.length &&
      types
        .map((t) => this.types.includes(t))
        .reduce((prev, curr) => prev && curr, true)
    ) {
      // If the two types arrays are the same (contains the same list of type values) then return the selection
      return this._selection
    }
    this._selection = types
      .map((t) => this.typeMap.get(t))
      .filter((t) => !!t)
      .reduce((prev, curr) => prev.concat([...curr]), [])
    this.types = types
    console.log(
      'rec-cache.rec-cache-type-selector.type-selection.query.selection',
      this._selection,
      this.typeMap
    )

    if (this.refreshFn) this.refreshFn(this._selection)
    return this._selection
  }

  add(id, type) {
    if (this.types.length == 0 || this.types.includes(type)) {
      this._selection.push(id)
      if (this.refreshFn) this.refreshFn(this._selection)
    }
  }

  delete(id) {
    if (this._selection.includes(id)) {
      this._selection = this._selection.filter((i) => i != id)
      if (this.refreshFn) this.refreshFn(this._selection)
    }
  }

  clear() {
    this._selection = []
    if (this.refreshFn) this.refreshFn(this._selection)
  }
}

class TypeSelector {
  constructor(dbs) {
    console.log('rec-cache.rec-cacher-type-selector.type-selector.constructor')

    // Maps a type value to a set of id values, type string -> set -> id string
    // TODO consider how to best expose on class, should likely be Iris, perhaps arrays rather than sets... also if/how to support reactivity
    this.typeMap = new Map()
    this.ids = dbs.ids
    this._states = []
    // Track which IDs have been processed to distinguish add vs update
    this.processedIds = new Set()

    addEventListener(CacheUpdateEvent, this.update.bind(this))
    addEventListener(CacheDeleteEvent, this.delete.bind(this))
  }

  update({ detail: individual }) {
    const id = individual.id.value
    const isUpdate = this.processedIds.has(id)

    const type = individual.type.value
    if (type) {
      if (this.typeMap.has(type)) {
        // The map get will return the set holding ids, to which the id is then added.
        this.typeMap.get(type).add(id)
      } else {
        this.typeMap.set(type, new Set().add(id))
      }
    }

    // Only notify selections for new individuals, not updates
    // Types don't change in WoB, so updates don't affect selections
    if (!isUpdate) {
      this.processedIds.add(id)
      this._states.forEach((s) => s.add(id, type))
    }
  }

  delete({ detail: id }) {
    const typesToDelete = []
    this.typeMap.forEach((ids, type) => {
      if (ids.has(id)) {
        if (ids.size == 1) {
          typesToDelete.push(type)
        } else {
          ids.delete(id)
        }
      }
    })
    typesToDelete.forEach((t) => this.typeMap.delete(t))
    this.processedIds.delete(id)

    this._states.forEach((s) => s.delete(id))
  }

  clear() {
    this.typeMap.clear()
    this.processedIds.clear()
    this._states.forEach((s) => s.clear())
  }

  createSelection(refreshFn = null) {
    const selection = new TypeSelection(this.typeMap, this.ids, refreshFn)
    this._states.push(selection)
    return selection
  }
}

export { TypeSelector }
